import { StateCreator } from "zustand";
import { GlobalState } from "../globalStore";
import type { Thought, SortOrder } from "@/core/fastapiClient/sdk";
import { sortThoughtsByDate } from "@/state/utils/sortThoughts";

export interface UnconfirmedThoughtsState {
  data: Thought[];
  isLoading: boolean;
  error: string | null;
}

export type RemoveUnconfirmedThoughtResult = {
  success: boolean;
  unconfirmedThought?: Thought;
};

export interface UnconfirmedThoughtSlice {
  unconfirmedThoughts: UnconfirmedThoughtsState;
  setUnconfirmedThoughts: (thoughts: Thought[], sortOrder?: SortOrder) => void;
  setUnconfirmedThoughtsLoading: (isLoading: boolean) => void;
  setUnconfirmedThoughtsError: (error: string | null) => void;
  removeUnconfirmedThought: (
    thoughtId: string
  ) => RemoveUnconfirmedThoughtResult;
  addUnconfirmedThought: (thought: Thought, sortOrder?: SortOrder) => void;
  updateUnconfirmedThought: (
    updatedThought: Thought,
    sortOrder?: SortOrder
  ) => void;
  resetUnconfirmedThoughts: () => void;
  // Initial state loading
  initFetchedUnconfirmedThoughts: boolean;
  setInitFetchedUnconfirmedThoughts: (initFetched: boolean) => void;
  // thought confirmation
  checkedUnconfirmedThoughts: string[]; // Array of thought IDs
  addCheckedUnconfirmedThought: (thoughtID: string) => void; // Add specifically
  removeCheckedUnconfirmedThought: (thoughtID: string) => void; // Remove specifically
  resetCheckedUnconfirmedThoughts: () => void;
}
export const createUnconfirmedThoughtSlice: StateCreator<
  GlobalState,
  [],
  [],
  UnconfirmedThoughtSlice
> = (set) => ({
  unconfirmedThoughts: {
    data: [],
    isLoading: false,
    error: null,
  },

  setUnconfirmedThoughts: (data, sortOrder) =>
    set((state) => {
      // Sort the array based on the latest_at timestamp
      const sortedThoughts = sortThoughtsByDate(data, sortOrder);

      return {
        unconfirmedThoughts: {
          ...state.unconfirmedThoughts, // Preserve any existing properties
          data: sortedThoughts,
          isLoading: false,
          error: null,
        },
      };
    }),
  setUnconfirmedThoughtsLoading: (isLoading) =>
    set((state) => ({
      unconfirmedThoughts: { ...state.unconfirmedThoughts, isLoading },
    })),
  setUnconfirmedThoughtsError: (error) =>
    set((state) => ({
      unconfirmedThoughts: { ...state.unconfirmedThoughts, error },
    })),
  removeUnconfirmedThought: (thoughtId) => {
    let result: RemoveUnconfirmedThoughtResult = { success: false };
    set((state) => {
      const thought = state.unconfirmedThoughts.data.find(
        (t) => t.id === thoughtId
      );
      const filteredThoughts = state.unconfirmedThoughts.data.filter(
        (t) => t.id !== thoughtId
      );
      result = { success: true, unconfirmedThought: thought };
      return {
        unconfirmedThoughts: {
          ...state.unconfirmedThoughts,
          data: filteredThoughts,
        },
      };
    });
    return result;
  },
  addUnconfirmedThought: (thought, sortOrder) =>
    set((state) => {
      // Add the new thought to the array
      const updatedThoughts = [...state.unconfirmedThoughts.data, thought];

      const sortedThoughts = sortThoughtsByDate(updatedThoughts, sortOrder);

      // Return the updated state with sorted thoughts
      return {
        unconfirmedThoughts: {
          ...state.unconfirmedThoughts,
          data: sortedThoughts,
        },
      };
    }),
  updateUnconfirmedThought: (updatedThought: Thought, sortOrder?: SortOrder) =>
    set((state) => {
      // Find the index of the thought to update
      const thoughtIndex = state.unconfirmedThoughts.data.findIndex(
        (t) => t.id === updatedThought.id
      );

      // If the thought doesn't exist, just return the current state
      if (thoughtIndex === -1) {
        return state;
      }

      // Create a new array with the updated thought
      const updatedThoughts = [...state.unconfirmedThoughts.data];
      updatedThoughts[thoughtIndex] = updatedThought;

      // Sort if needed
      const sortedThoughts = sortOrder
        ? sortThoughtsByDate(updatedThoughts, sortOrder)
        : updatedThoughts;

      return {
        unconfirmedThoughts: {
          ...state.unconfirmedThoughts,
          data: sortedThoughts,
        },
      };
    }),

  resetUnconfirmedThoughts: () =>
    set({
      unconfirmedThoughts: { data: [], isLoading: false, error: null },
    }),

  // Initial state loading
  initFetchedUnconfirmedThoughts: false,
  setInitFetchedUnconfirmedThoughts: (initFetched) =>
    set({
      initFetchedUnconfirmedThoughts: initFetched,
    }),

  // thought confirmation
  checkedUnconfirmedThoughts: [],
  addCheckedUnconfirmedThought: (thoughtID) =>
    set((state) => ({
      checkedUnconfirmedThoughts: [
        ...state.checkedUnconfirmedThoughts,
        thoughtID,
      ],
    })),
  removeCheckedUnconfirmedThought: (thoughtID) =>
    set((state) => ({
      checkedUnconfirmedThoughts: state.checkedUnconfirmedThoughts.filter(
        (id) => id !== thoughtID
      ),
    })),
  resetCheckedUnconfirmedThoughts: () =>
    set({ checkedUnconfirmedThoughts: [] }),
});
